// System
import React, { useMemo, useEffect } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';
import { formatPaginationTotal } from '@utils/data/number/localeNumberFormat';

//  Table
import { useFreebetUsageTableColumns } from './useFreebetsUsageTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useFreebetsUsageTable } from '@Modal/hooks/freebetsUsage/useFreebetsUsage';

// GraphQL
import { usePaginatedFreeBetsUsageLogsQuery } from '@Src/generated.graphql';

// Types
import { FreebetUsageItemDto } from '@contracts/api/dto/freebetUsage/FreebetUsageDto';

export const FreebetsUsageTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    total,
    limit,
    page,
    setPage,
    setTotal,
    setLimit,
    columnVisibility,
    visiblityCustomizable,
    freebetsUsageListFilters,
    freebetUsageTableFilters,
  } = useFreebetsUsageTable();
  const columns = useFreebetUsageTableColumns();
  const { userPermissions } = useAuthorization();
  const permissionToViewUsageLogs = userPermissions?.includes('CAN_VIEW_FREEBETS_USAGE_LOGS');
  const { data, loading, refetch } = usePaginatedFreeBetsUsageLogsQuery({
    variables: {
      limit,
      page,
      filter: freebetUsageTableFilters,
    },
    skip: !permissionToViewUsageLogs,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, freebetsUsageListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedFreeBetsUsageLogs) {
      const { total, limit, page } = data.paginatedFreeBetsUsageLogs;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data, dispatch]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewUsageLogs) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }} pageNumber={1}>
        <TableEx name="FreebetsUsageList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedFreeBetsUsageLogs?.docs?.map((row: FreebetUsageItemDto) => (
              <TableRowPresenter
                id={row._id}
                key={row._id}
                columns={visibleColumns}
                row={row}
                onClick={() => {}}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="FreebetsUsageFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${formatPaginationTotal(count)}`
          }
        />
      </TableEx>
    </>
  );
};
